import React, { RefObject } from "react";
import { type Option } from "@sprout/select";
import ReactSelect, { components, type SingleValue, type SingleValueProps } from "react-select";
import intlTelInput from "intl-tel-input";
import { SelectWrapper } from "@sprout/select/internal/select_wrapper.tsx";
import { useSharedReactSelectProps } from "@sprout/select/internal/hooks/use_shared_react_select_props.ts";

interface Props {
  id?: string;
  label: string;
  value: Option | null;
  error: string | null;
  onChange: (option: Option | null) => void;
  ref: RefObject<HTMLDivElement | null>;
}

const OPTIONS: Option[] = intlTelInput.getCountryData().map((country) => ({
  label: `${country.name} +${country.dialCode}`,
  value: country.iso2,
}));

const PRIORITY_COUNTRIES = ["us"];
for (const country of PRIORITY_COUNTRIES) {
  const index = OPTIONS.findIndex((option) => option.value === country);
  if (index > -1) {
    const option = OPTIONS[index];
    OPTIONS.splice(index, 1);
    OPTIONS.unshift(option);
  }
}

const DIAL_CODES = Object.fromEntries(
  intlTelInput.getCountryData().map((country) => [country.iso2, country.dialCode]),
);

const Flag = ({ iso2, a11yText }: { iso2: string; a11yText?: string }) => {
  // This markup is copied from the intl-tel-input dropdown that we're hiding
  return (
    <div className={`iti__flag iti__${iso2}`}>
      {a11yText && (
        <span className="iti__a11y-text" id="xxx">
          {a11yText}
        </span>
      )}
    </div>
  );
};

const Country = (option: Option) => {
  return (
    <>
      <Flag iso2={option.value.toString()} /> {option.label}
    </>
  );
};

const SelectedCountry: React.FC<SingleValueProps<Option, false>> = (props) => {
  const option = props.data;
  return (
    <components.SingleValue {...props}>
      <Flag iso2={option.value.toString()} a11yText={option.label} />
      <span>+{DIAL_CODES[option.value]}</span>
    </components.SingleValue>
  );
};

export const CountryInput = ({ label, value, error, onChange, ref }: Props) => {
  const { id, ...sharedReactSelectProps } = useSharedReactSelectProps(
    (newValue) => onChange(newValue as SingleValue<Option>),
    { error, label, menuWidth: 300 },
  );

  sharedReactSelectProps.components ||= {};
  sharedReactSelectProps.components.SingleValue = SelectedCountry;

  return (
    <SelectWrapper selectId={id} label={label} error={error} ref={ref}>
      <ReactSelect
        id={id}
        {...sharedReactSelectProps}
        options={OPTIONS}
        value={value}
        formatOptionLabel={Country}
      />
    </SelectWrapper>
  );
};
